/**
 * MigrationModeAdmin (Iter 80.3)
 *
 * Primary-Super-Admin-only admin page for the tenant-level Migration
 * Mode flag. Three states drive three distinct UI panels:
 *
 *   - NOT_STARTED  → "Enable Migration Mode" CTA + activation form
 *   - ACTIVE       → status panel + "Sign off & complete migration"
 *                    multi-step modal showing the corrections summary
 *                    and requiring the typed confirmation phrase
 *   - SIGNED_OFF   → read-only certificate-of-completion panel
 *
 * Sign-off is irreversible — once committed, future migration
 * discrepancies must use Void + Re-record.
 */
import { useEffect, useMemo, useState } from "react";
import {
    Database, ShieldCheck, Sparkle, CheckCircle, Warning,
    SealCheck, ArrowRight,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatDate, formatDateTime } from "../../lib/format";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { DialogFooter } from "../../components/ui/dialog";
import PageHeader from "../../components/layout/PageHeader";
import FormDialog, { FormDialogCancel } from "../../components/FormDialog";
import { toast } from "sonner";

const REASONS = [
    { value: "InitialMigration",    label: "Initial migration — onboarding data import" },
    { value: "HistoricalDataImport", label: "Historical data import — backfill legacy records" },
    { value: "AcquisitionMerger",    label: "Acquisition / merger — incoming dataset" },
    { value: "Other",                label: "Other (explain in the note)" },
];

export default function MigrationModeAdmin() {
    const { user } = useAuth();
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);

    // Enable form state
    const [enableOpen, setEnableOpen] = useState(false);
    const [enReason, setEnReason] = useState("");
    const [enNote, setEnNote] = useState("");
    const [enCloseDate, setEnCloseDate] = useState("");
    const [enBusy, setEnBusy] = useState(false);

    // Sign-off form state
    const [signoffOpen, setSignoffOpen] = useState(false);
    const [soStep, setSoStep] = useState(1);
    const [soPreview, setSoPreview] = useState(null);
    const [soPreviewBusy, setSoPreviewBusy] = useState(false);
    const [soPhrase, setSoPhrase] = useState("");
    const [soNote, setSoNote] = useState("");
    const [soAck, setSoAck] = useState(false);
    const [soBusy, setSoBusy] = useState(false);

    const isPrimaryAdmin = !!user?.is_primary_admin;

    const reload = async () => {
        try {
            const { data } = await api.get("/billing/migration-mode/status");
            setStatus(data);
        } catch (e) {
            toast.error(formatApiError(e, "Failed to load migration status"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Defer the initial reload via a post-commit microtask so the
        // React 19 react-hooks/set-state-in-effect lint rule accepts
        // the state-sets in `reload()`.
        Promise.resolve().then(() => { reload(); });
    }, []);

    const submitEnable = async () => {
        if (!enReason || enNote.trim().length < 20) return;
        setEnBusy(true);
        try {
            const body = {
                reason: enReason, note: enNote.trim(),
                ...(enCloseDate ? { estimated_close_date: enCloseDate } : {}),
            };
            await api.post("/billing/migration-mode/enable", body);
            toast.success("Migration Mode ACTIVATED");
            setEnableOpen(false);
            setEnReason(""); setEnNote(""); setEnCloseDate("");
            await reload();
        } catch (e) {
            toast.error(formatApiError(e, "Failed to enable Migration Mode"));
        } finally {
            setEnBusy(false);
        }
    };

    useEffect(() => {
        // Fetch preview on entering sign-off step 2
        if (!signoffOpen || soStep !== 2) return;
        let alive = true;
        // React 19 react-hooks/set-state-in-effect: defer state-sets
        // to the post-commit microtask so the rule's heuristic accepts.
        Promise.resolve().then(() => { if (alive) setSoPreviewBusy(true); });
        api.get("/billing/migration-mode/signoff/preview")
            .then((r) => { if (alive) setSoPreview(r.data); })
            .catch((e) => { if (alive) toast.error(formatApiError(e, "Failed to load summary")); })
            .finally(() => { if (alive) setSoPreviewBusy(false); });
        return () => { alive = false; };
    }, [signoffOpen, soStep]);

    const submitSignoff = async () => {
        if (soPhrase !== "SIGN OFF MIGRATION") return;
        if (!soAck || soNote.trim().length < 20) return;
        setSoBusy(true);
        try {
            await api.post("/billing/migration-mode/sign-off", {
                confirmation_phrase: "SIGN OFF MIGRATION",
                note: soNote.trim(),
                acknowledge_irreversible: true,
            });
            toast.success("Migration Mode signed off — terminal state reached");
            setSignoffOpen(false);
            setSoStep(1); setSoPhrase(""); setSoNote(""); setSoAck(false);
            await reload();
        } catch (e) {
            toast.error(formatApiError(e, "Failed to sign off"));
        } finally {
            setSoBusy(false);
        }
    };

    const closeEnable = () => {
        if (enBusy) return;
        setEnableOpen(false);
        setEnReason(""); setEnNote(""); setEnCloseDate("");
    };
    const closeSignoff = () => {
        if (soBusy) return;
        setSignoffOpen(false);
        setSoStep(1); setSoPhrase(""); setSoNote(""); setSoAck(false);
    };

    if (loading) {
        return <div className="p-12 text-center text-sm text-slate-500">Loading…</div>;
    }
    if (!isPrimaryAdmin) {
        return (
            <div className="p-12 text-center" data-testid="migration-mode-rbac-refused">
                <ShieldCheck size={32} className="mx-auto text-slate-300" weight="duotone" />
                <div className="mt-2 text-sm text-slate-500">
                    Migration Mode administration is restricted to Primary Super Admin.
                </div>
            </div>
        );
    }

    const state = status?.state || "not_started";

    return (
        <div className="p-6 sm:p-8 max-w-4xl">
            <PageHeader
                title="Migration Mode"
                subtitle="Tenant-level one-shot toggle that permits cross-FY payment-date corrections during the onboarding / data-migration window. Irreversible once signed off."
                icon={<Database size={22} weight="duotone" />}
            />

            <div className="mt-6">
                {state === "not_started" && (
                    <NotStartedPanel onEnable={() => setEnableOpen(true)} />
                )}
                {state === "active" && (
                    <ActivePanel status={status} onSignOff={() => { setSignoffOpen(true); setSoStep(1); }} />
                )}
                {state === "signed_off" && (
                    <SignedOffPanel status={status} />
                )}
            </div>

            {/* Enable dialog */}
            <FormDialog
                open={enableOpen}
                onOpenChange={(v) => !v && closeEnable()}
                isDirty={!!enReason || enNote.length > 0 || enCloseDate.length > 0}
                contentClassName="max-w-lg"
                testId="enable-migration-mode-dialog"
                srTitle="Enable Migration Mode"
                srDescription="Activate the tenant-level Migration Mode toggle. Once activated, cross-FY payment-date edits are permitted and audit-tagged."
                header={
                    <div className="space-y-1.5">
                        <div className="flex items-center gap-2 text-lg font-semibold">
                            <Sparkle size={18} weight="duotone" className="text-rose-600" />
                            Enable Migration Mode
                        </div>
                        <div className="text-xs text-slate-500">
                            Activates the tenant-level flag. Cross-FY date edits will be permitted
                            and audit-tagged with `migration_mode_correction = true`. GST-filed
                            invoices and TDS-Claimed settlements remain protected.
                        </div>
                    </div>
                }
            >
                <div className="space-y-4 py-2">
                    <div>
                        <Label className="text-xs font-semibold">Reason *</Label>
                        <select
                            value={enReason}
                            onChange={(e) => setEnReason(e.target.value)}
                            className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400/40"
                            data-testid="enable-mm-reason-select"
                        >
                            <option value="">— Pick a reason —</option>
                            {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Activation note *</Label>
                        <textarea
                            value={enNote}
                            onChange={(e) => setEnNote(e.target.value)}
                            placeholder="Explain why we are entering Migration Mode (≥20 chars)"
                            rows={3}
                            maxLength={1000}
                            className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400/40"
                            data-testid="enable-mm-note-input"
                        />
                        <div className="text-[10px] text-slate-400 mt-1">{enNote.length} / 1000 · min 20 chars</div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Estimated close date <span className="text-slate-400">(informational)</span></Label>
                        <Input type="date" value={enCloseDate} onChange={(e) => setEnCloseDate(e.target.value)} className="mt-1" data-testid="enable-mm-close-date-input" />
                    </div>
                </div>
                <DialogFooter className="border-t border-slate-100 -mx-6 px-6 pt-4 mt-2">
                    <FormDialogCancel disabled={enBusy} testId="enable-mm-cancel-btn" />
                    <Button onClick={submitEnable}
                            disabled={enBusy || !enReason || enNote.trim().length < 20}
                            className="bg-rose-600 hover:bg-rose-500 text-white gap-1.5"
                            data-testid="enable-mm-confirm-btn">
                        <Sparkle size={14} />
                        {enBusy ? "Activating…" : "Activate Migration Mode"}
                    </Button>
                </DialogFooter>
            </FormDialog>

            {/* Sign-off dialog */}
            <FormDialog
                open={signoffOpen}
                onOpenChange={(v) => !v && closeSignoff()}
                isDirty={soStep === 2 && (soPhrase.length > 0 || soNote.length > 0 || soAck)}
                contentClassName="max-w-2xl"
                testId="signoff-migration-mode-dialog"
                srTitle="Sign off Migration Mode"
                srDescription="Irreversibly transition Migration Mode to the SIGNED_OFF terminal state. Cannot be reversed."
                header={
                    <div className="space-y-1.5">
                        <div className="flex items-center gap-2 text-lg font-semibold">
                            <SealCheck size={18} weight="duotone" className="text-emerald-700" />
                            Sign off Migration Mode
                            <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-rose-50 text-rose-700 border border-rose-200">
                                Irreversible
                            </span>
                        </div>
                        <div className="text-xs text-slate-500">
                            Locks the tenant out of Migration Mode forever. Future migration
                            discrepancies must use Void + Re-record.
                        </div>
                    </div>
                }
            >
                {soStep === 1 && (
                    <div className="space-y-4 py-2" data-testid="signoff-step1">
                        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-900">
                            <div className="flex items-start gap-2">
                                <Warning size={18} weight="fill" />
                                <div>
                                    <div className="font-semibold">This is a one-way transition.</div>
                                    <div className="text-xs mt-1 leading-relaxed">
                                        Once signed off, Migration Mode cannot be re-enabled. The
                                        cross-FY date-edit override will no longer be available; if
                                        new migration corrections are needed, they must be done via
                                        Void + Re-record (which creates new audit-trail entries
                                        OUTSIDE the migration-mode-correction tag).
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="text-sm text-slate-700">
                            Click <b>Continue</b> to load a sign-off certificate summary showing
                            the corrections made during the migration window.
                        </div>
                    </div>
                )}
                {soStep === 2 && (
                    <div className="space-y-4 py-2" data-testid="signoff-step2">
                        {soPreviewBusy && <div className="text-sm text-slate-500 text-center py-6">Computing certificate summary…</div>}
                        {!soPreviewBusy && soPreview && (
                            <SignoffCertificate summary={soPreview} />
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-semibold">Type <span className="font-mono text-rose-700">SIGN OFF MIGRATION</span> to confirm *</Label>
                                <Input
                                    value={soPhrase}
                                    onChange={(e) => setSoPhrase(e.target.value)}
                                    placeholder="SIGN OFF MIGRATION"
                                    className="mt-1 font-mono"
                                    data-testid="signoff-phrase-input"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Sign-off note *</Label>
                                <Input
                                    value={soNote}
                                    onChange={(e) => setSoNote(e.target.value)}
                                    placeholder="What's been completed / signed off (≥20 chars)"
                                    className="mt-1"
                                    data-testid="signoff-note-input"
                                />
                            </div>
                        </div>
                        <label className="flex items-start gap-2 cursor-pointer text-[12px] text-slate-800 border-t border-slate-100 pt-3">
                            <input
                                type="checkbox"
                                checked={soAck}
                                onChange={(e) => setSoAck(e.target.checked)}
                                className="mt-0.5 accent-rose-600"
                                data-testid="signoff-acknowledge"
                            />
                            <span>I understand this action is irreversible — once signed off, Migration Mode cannot be re-enabled and the cross-FY override is permanently disabled.</span>
                        </label>
                    </div>
                )}
                <DialogFooter className="border-t border-slate-100 -mx-6 px-6 pt-4 mt-2">
                    <FormDialogCancel disabled={soBusy} testId="signoff-cancel-btn" />
                    {soStep === 1 ? (
                        <Button onClick={() => setSoStep(2)} disabled={soBusy} className="bg-emerald-700 hover:bg-emerald-600 text-white gap-1.5" data-testid="signoff-next-btn">
                            Continue <ArrowRight size={14} />
                        </Button>
                    ) : (
                        <>
                            <Button onClick={() => setSoStep(1)} disabled={soBusy} variant="outline" data-testid="signoff-back-btn">Back</Button>
                            <Button
                                onClick={submitSignoff}
                                disabled={soBusy || soPhrase !== "SIGN OFF MIGRATION" || soNote.trim().length < 20 || !soAck}
                                className="bg-emerald-700 hover:bg-emerald-600 text-white gap-1.5"
                                data-testid="signoff-confirm-btn"
                            >
                                <SealCheck size={14} weight="fill" />
                                {soBusy ? "Signing off…" : "Sign off Migration"}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </FormDialog>
        </div>
    );
}

function NotStartedPanel({ onEnable }) {
    return (
        <div className="rounded-lg border-2 border-dashed border-slate-200 bg-slate-50/50 p-8 text-center" data-testid="mm-state-not-started">
            <Database size={36} className="mx-auto text-slate-300" weight="duotone" />
            <div className="mt-3 text-sm text-slate-600">
                Migration Mode has <b>not been activated</b> for this tenant.
            </div>
            <div className="mt-1 text-xs text-slate-500">
                Activate during the onboarding / migration window to permit cross-FY payment-date
                corrections. Sign-off is required to complete the migration.
            </div>
            <Button onClick={onEnable} className="mt-4 bg-rose-600 hover:bg-rose-500 text-white gap-1.5" data-testid="enable-migration-mode-cta">
                <Sparkle size={14} /> Enable Migration Mode
            </Button>
        </div>
    );
}

function ActivePanel({ status, onSignOff }) {
    return (
        <div className="space-y-4" data-testid="mm-state-active">
            <div className="rounded-lg border-2 border-rose-300 bg-rose-50 p-5">
                <div className="flex items-start gap-3">
                    <Sparkle size={22} weight="fill" className="text-rose-700 shrink-0 mt-0.5" />
                    <div className="grow">
                        <div className="text-sm font-bold text-rose-900">Migration Mode is ACTIVE</div>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-1 mt-2 text-xs text-rose-900">
                            <div>Activated</div><div className="font-mono">{formatDateTime(status.activated_at)}</div>
                            <div>Activated by</div><div className="font-mono">{status.activated_by_email}</div>
                            <div>Reason</div><div>{status.activation_reason}</div>
                            {status.estimated_close_date && (<>
                                <div>Estimated close</div><div className="font-mono">{formatDate(status.estimated_close_date)}</div>
                            </>)}
                        </div>
                        {status.activation_note && (
                            <div className="mt-3 text-xs italic text-rose-900/80 border-t border-rose-200 pt-2">
                                &ldquo;{status.activation_note}&rdquo;
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="rounded-lg border border-slate-200 p-5 bg-white">
                <div className="text-xs text-slate-600 leading-relaxed">
                    Cross-FY payment-date edits are currently permitted and audit-tagged with
                    <code className="text-[10px] px-1 py-0.5 mx-1 rounded bg-slate-100 font-mono">migration_mode_correction = true</code>.
                    Every payment row corrected under Migration Mode also carries a <b>MIGRATION CORRECTION</b> badge in the receipts log.
                </div>
                <div className="text-xs text-slate-600 mt-2 leading-relaxed">
                    GST-filed invoices and TDS-Claimed settlements remain <b>protected even under Migration Mode</b>.
                </div>
            </div>
            <Button onClick={onSignOff} className="bg-emerald-700 hover:bg-emerald-600 text-white gap-1.5" data-testid="signoff-migration-mode-cta">
                <SealCheck size={14} weight="fill" /> Sign off &amp; complete migration
            </Button>
        </div>
    );
}

function SignedOffPanel({ status }) {
    return (
        <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-6" data-testid="mm-state-signed-off">
            <div className="flex items-start gap-3">
                <SealCheck size={28} weight="fill" className="text-emerald-700 shrink-0" />
                <div className="grow">
                    <div className="text-base font-bold text-emerald-900">Migration sign-off complete</div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 mt-3 text-xs text-emerald-900">
                        <div>Migration activated</div><div className="font-mono">{formatDateTime(status.activated_at)} by {status.activated_by_email}</div>
                        <div>Signed off</div><div className="font-mono">{formatDateTime(status.signed_off_at)} by {status.signed_off_by_email}</div>
                        {status.signoff_note && (<>
                            <div>Sign-off note</div><div className="italic">&ldquo;{status.signoff_note}&rdquo;</div>
                        </>)}
                    </div>
                    {status.signoff_summary && (
                        <div className="mt-4 border-t border-emerald-200 pt-3">
                            <SignoffCertificate summary={status.signoff_summary} compact />
                        </div>
                    )}
                    <div className="mt-4 text-[11px] text-emerald-900/80 leading-relaxed">
                        Cross-FY date edits are now permanently blocked. Any future migration
                        discrepancies must use Void + Re-record.
                    </div>
                </div>
            </div>
        </div>
    );
}

function SignoffCertificate({ summary, compact = false }) {
    const stat = (label, value, testid) => (
        <div className={"rounded border border-slate-200 p-2.5 bg-white " + (compact ? "" : "")}>
            <div className="text-[10px] reedos-eyebrow text-slate-500">{label}</div>
            <div className="mt-1 text-sm font-mono font-semibold text-slate-900" data-testid={testid}>{value}</div>
        </div>
    );
    return (
        <div className="space-y-3" data-testid="signoff-certificate">
            <div className="text-[10px] reedos-eyebrow text-slate-500">Tenant state at sign-off</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {stat("Invoices", summary.invoices_total, "mm-summary-invoices-total")}
                {stat("With outstanding", summary.invoices_with_outstanding, "mm-summary-invoices-outstanding")}
                {stat("GST-filed", summary.invoices_gst_filed, "mm-summary-gst-filed")}
                {stat("Payments", summary.payments_total, "mm-summary-payments-total")}
                {stat("Voided settlements", summary.payments_voided, "mm-summary-payments-voided")}
                {stat("Corrected settlements", summary.payments_corrected, "mm-summary-payments-corrected")}
                {stat("TDS-claimed", summary.payments_tds_claimed, "mm-summary-tds-claimed")}
                {stat("Cross-FY corrections", summary.cross_fy_corrections, "mm-summary-cross-fy")}
            </div>
            {summary.actions_breakdown && Object.keys(summary.actions_breakdown).length > 0 && (
                <div>
                    <div className="text-[10px] reedos-eyebrow text-slate-500 mb-1.5">Migration-mode corrections by action</div>
                    <div className="rounded border border-slate-200 bg-white">
                        {Object.entries(summary.actions_breakdown).map(([action, count]) => (
                            <div key={action} className="flex items-center justify-between text-xs border-b border-slate-100 last:border-b-0 px-3 py-1.5" data-testid={`mm-summary-action-${action}`}>
                                <span className="font-mono text-slate-700">{action}</span>
                                <span className="font-mono font-semibold text-slate-900">{count}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
